import axios from 'axios';
import { toast } from 'react-toastify';

// Create axios instance with base URL and headers
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
  withCredentials: true, // Important for sending cookies with CORS
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Add these options to handle CORS properly
  crossDomain: true,
  responseType: 'json',
});

// Flag to prevent multiple token refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle network errors (e.g., when backend is down)
    if (error.code === 'ERR_NETWORK') {
      toast.error('Unable to connect to the server. Please check your connection and try again.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return Promise.reject(error);
    }
    
    // Handle 400 Bad Request with duplicate email error
    if (error.response?.status === 400 && 
        (error.response?.data?.detail === 'A customer with this email already exists' ||
         error.response?.data?.message === 'A customer with this email already exists')) {
      toast.error('A customer with this email already exists', {
        position: 'top-right',
        autoClose: 7000, // Show for 7 seconds since this is an important message
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return Promise.reject(error);
    }
    
    // Handle 5xx server errors (including uvicorn errors)
    // if (error.response?.status >= 500) {
    //   const errorMessage = error.response?.data?.detail.reason || 'An unexpected server error occurred. Please try again later.';
    //   toast.error(`Server Error: ${errorMessage}`, {
    //     position: 'top-right',
    //     autoClose: 5000,
    //     hideProgressBar: false,
    //     closeOnClick: true,
    //     pauseOnHover: true,
    //     draggable: true,
    //     progress: undefined,
    //   });
    //   return Promise.reject(error);
    // }
    
    // If the error status is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing the token, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
        if (!refreshToken) {
          // No refresh token available, redirect to login
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Try to refresh the token
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/refresh-token`,
          { refresh_token: refreshToken }
        );

        const { access_token, refresh_token } = response.data;
        
        // Store the new tokens
        // Use the same storage as the original token
        const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
        storage.setItem('token', access_token);
        if (refresh_token) {
          storage.setItem('refresh_token', refresh_token);
        }

        // Update the Authorization header
        api.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        originalRequest.headers['Authorization'] = 'Bearer ' + access_token;

        // Process any queued requests
        processQueue(null, access_token);
        isRefreshing = false;

        // Retry the original request
        return api(originalRequest);
      } catch (error) {
        // If refresh fails, clear tokens and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh_token');
        window.location.href = '/login';
        processQueue(error, null);
        isRefreshing = false;
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Usage Analytics API
export const getUsageAnalytics = async (params = {}) => {
  try {
    const { page = 1, perPage = 10, sortField = 'date', sortDir = 'desc' } = params;
    const response = await api.get('/analytics/usage', {
      params: {
        page,
        per_page: perPage,
        sort_field: sortField,
        sort_dir: sortDir,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching usage analytics:', error);
    throw error;
  }
};

// License Usage Summary API
export const getLicenseUsageSummary = async (params = {}) => {
  try {
    const { startDate, endDate } = params;
    const response = await api.get('/analytics/license-summary', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching license usage summary:', error);
    throw error;
  }
};

// Customer License Usage API
export const getCustomerLicenseUsage = async (params = {}) => {
  try {
    const { page = 1, perPage = 10, sortField = 'customerName', sortDir = 'asc' } = params;
    const response = await api.get('/analytics/customer-licenses', {
      params: {
        page,
        per_page: perPage,
        sort_field: sortField,
        sort_dir: sortDir,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching customer license usage:', error);
    throw error;
  }
};

// Customer License Totals (Aggregated) API
export const getCustomerLicenseMetrics = async () => {
  try {
    const response = await api.get('/analytics/customer-license-metrics');
    return response.data;
  } catch (error) {
    console.error('Error fetching customer license metrics:', error);
    throw error;
  }
};

// Validation Trends API
export const getValidationTrends = async (params = {}) => {
  try {
    const { days = 30 } = params;
    const response = await api.get('/analytics/validation-trends', {
      params: { days },
    });
    
    // Transform the response to match the expected format
    const data = response.data;
    return {
      labels: data.labels || [],
      successful: data.successful || [],
      failed: data.failed || [],
      heatmap: data.heatmap || [],
      successRate: data.success_rate,
      failureRate: data.failure_rate,
      totalValidations: data.total_validations || 0,
    };
  } catch (error) {
    console.error('Error fetching validation trends:', error);
    throw error;
  }
};

// Change Logs API
export const getChangeLogs = async (params = {}) => {
  try {
    const { 
      page = 1, 
      perPage = 10, 
      type = 'all',
      startDate,
      endDate,
      action,
      userId
    } = params;
    
    const response = await api.get('/analytics/change-logs', {
      params: {
        page,
        per_page: perPage,
        type: type === 'all' ? undefined : type,
        start_date: startDate,
        end_date: endDate,
        action,
        user_id: userId,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching change logs:', error);
    throw error;
  }
};

// Settings API
const settings = {
  // Get all settings
  getSettings: async () => {
    try {
      const response = await api.get('/settings/');
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  // Update site title
  updateSiteTitle: async (title, updatedBy) => {
    const payload = {
      value: title,
      updated_by: updatedBy ? parseInt(updatedBy) : 1
    };
    
    console.log('Updating site title with payload:', payload);
    
    try {
      const response = await api.put('/settings/site-title', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        validateStatus: (status) => status < 500 // Don't throw for 4xx errors
      });
      
      console.log('Site title update response:', response);
      
      if (response.status >= 400) {
        const error = new Error(response.data?.detail || 'Failed to update site title');
        error.response = response;
        throw error;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating site title:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      });
      throw error;
    }
  },

  // Update admin email
  updateAdminEmail: async (email, updatedBy) => {
    try {
      const response = await api.put('/settings/admin-email', {
        admin_email: email,
        updated_by: updatedBy ? parseInt(updatedBy) : 1
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating admin email:', error);
      throw error;
    }
  },

  // Update timezone
  updateTimezone: async (timezone, updatedBy) => {
    try {
      const response = await api.put('/settings/timezone', {
        timezone: timezone || 'UTC',
        updated_by: updatedBy ? parseInt(updatedBy) : 1
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating timezone:', error);
      throw error;
    }
  },

  // Update registration settings
  updateRegistrationSettings: async (allowRegistration, updatedBy) => {
    try {
      const response = await api.put('/settings/registration', {
        allow_registration: Boolean(allowRegistration),
        updated_by: updatedBy ? parseInt(updatedBy) : 1
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating registration settings:', error);
      throw error;
    }
  },

  // Update email notifications
  updateEmailNotifications: async (enabled, updatedBy) => {
    try {
      const response = await api.put('/settings/email-notifications', {
        enabled: Boolean(enabled),
        updated_by: updatedBy ? parseInt(updatedBy) : 1
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating email notifications:', error);
      throw error;
    }
  },

  // Toggle maintenance mode
  toggleMaintenanceMode: async (enabled, updatedBy) => {
    try {
      // First try with PUT
      try {
        const response = await api.put('/settings/maintenance', {
          enabled: Boolean(enabled),
          message: enabled ? 'System is currently under maintenance. Please check back later.' : '',
          updated_by: updatedBy ? parseInt(updatedBy) : 1
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error) {
        // If PUT fails with 405, try with POST for backward compatibility
        if (error.response?.status === 405) {
          const response = await api.post('/settings/maintenance/toggle', {
            enabled: Boolean(enabled),
            message: enabled ? 'System is currently under maintenance. Please check back later.' : '',
            updated_by: updatedBy ? parseInt(updatedBy) : 1
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          });
          return response.data;
        }
        throw error;
      }
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      throw error;
    }
  },
  
  // Batch update multiple settings at once
  batchUpdateSettings: async (settings, updatedBy) => {
    const payload = {
      settings: settings,  // Wrap settings in a settings object
      updated_by: updatedBy ? parseInt(updatedBy) : 1
    };
    
    console.log('Batch updating settings with payload:', payload);
    
    try {
      const response = await api.put('/settings/batch-update', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        validateStatus: (status) => status < 500 // Don't throw for 4xx errors
      });
      
      console.log('Batch update response:', response);
      
      if (response.status >= 400) {
        const error = new Error(response.data?.detail || 'Failed to update settings');
        error.response = response;
        throw error;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating settings:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      });
      throw error;
    }
  },
};

// Security Settings API
const getSecuritySettings = async (signal) => {
  try {
    const response = await api.get('/settings/security/all', { signal });
    return response.data;
  } catch (error) {
    console.error('Error fetching security settings:', error);
    throw error;
  }
};

export const licenseMetrics = async () => {
    try {
        const response = await api.get('/analytics/license-metrics');
        return response.data;
    } catch (error) {
        console.error('Error fetching license metrics:', error);
        throw error;
    }
};


// Add settings to the API object
// Password Policy API
const passwordPolicy = {
  // Get current password policy
  get: async (signal) => {
    try {
      const response = await api.get('/settings/security/password-policy', { signal });
      return response.data;
    } catch (error) {
      console.error('Error fetching password policy:', error);
      throw error;
    }
  },
  
  // Update password policy
  update: async (data) => {
    try {
      
      //const userId = localStorage.getItem('userId') || 'system';
      const response = await api.put('/settings/security/password-policy', {
        ...data
        //updated_by: userId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating password policy:', error);
      throw error;
    }
  }
};

// Two-Factor Authentication API
const twoFactorAuth = {
  // Get current two-factor authentication settings
  get: async (signal) => {
    try {
      const response = await api.get('/settings/security/two-factor-auth', { signal });
      return response.data;
    } catch (error) {
      console.error('Error fetching two-factor authentication settings:', error);
      throw error;
    }
  },
  
  // Update two-factor authentication settings
  update: async (data) => {
    try {
      const response = await api.put('/settings/security/two-factor-auth', {
        ...data,
        updated_by: localStorage.getItem('userId') || ''
      });
      return response.data;
    } catch (error) {
      console.error('Error updating two-factor authentication settings:', error);
      throw error;
    }
  }
};

// License Encryption API
const licenseEncryption = {
  // Get current license encryption settings
  get: async (signal) => {
    try {
      const response = await api.get('/settings/license-encryption', { signal });
      return response.data;
    } catch (error) {
      console.error('Error fetching license encryption settings:', error);
      throw error;
    }
  },

  // Update license encryption settings
  update: async (data) => {
    try {
      const response = await api.put('/settings/license-encryption', data);
      return response.data;
    } catch (error) {
      console.error('Error updating license encryption settings:', error);
      throw error;
    }
  },

  // Rotate encryption keys
  rotateKeys: async () => {
    try {
      const response = await api.post('/settings/license-encryption/rotate-keys');
      return response.data;
    } catch (error) {
      console.error('Error rotating encryption keys:', error);
      throw error;
    }
  },

  // Get validation failures
  getValidationFailures: async (params = {}) => {
    try {
      const response = await api.get('/licenses/validation-failures', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching validation failures:', error);
      throw error;
    }
  }
};



/**
 * Update user's password
 * @param {Object} passwordData - Object containing current and new password
 * @param {string} passwordData.currentPassword - Current password
 * @param {string} passwordData.newPassword - New password
 * @param {AbortSignal} [signal] - Optional AbortSignal to cancel the request
 * @returns {Promise<Object>} Response data from the server
 */
const upDatePassword = async ({ currentPassword, newPassword }, signal) => {
  try {
    const response = await api.post('/auth/update-password', {
      current_password: currentPassword,
      new_password: newPassword
    }, { signal });
    return response.data;
  } catch (error) {
    console.error('Error updating password:', error);
    // Extract the error message from the response if available
    const errorMessage = error.response?.data?.detail?.message || 
                        error.response?.data?.detail || 
                        error.message || 
                        'Failed to update password';
    throw new Error(errorMessage);
  }
};


const getCustomerCount = async () => {
  try {
    const response = await api.get('/analytics/customers/count');
    return response.data;
  } catch (error) {
    console.error('Error fetching customer count:', error);
    throw error;
  }
};

/**
 * Fetches upcoming license expiries
 * @param {Object} params - Query parameters for filtering/sorting
 * @param {number} [params.limit=10] - Maximum number of results to return
 * @param {string} [params.sortBy=expiry_date] - Field to sort by
 * @param {string} [params.order=asc] - Sort order (asc/desc)
 * @returns {Promise<Array>} Array of license objects with expiry information
 */
export const getUpcomingExpiries = async (params = {}) => {
  try {
    const response = await api.get('/licenses/upcoming-expiries', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching upcoming license expiries:', error);
    throw error;
  }
};

// Add all API methods to the api object
// MAC Requests API
const getMacRequests = async (params = {}) => {
  try {
    const response = await api.get('/mac-requests', { 
      params: { 
        status: 'PENDING',
        skip: 0,
        limit: 10,
        ...params 
      } 
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching MAC requests:', error);
    throw error;
  }
};

// Add all API methods to the api object
Object.assign(api, {
  getMacRequests,
  upDatePassword,
  getCustomerCount,
  getUpcomingExpiries,
  settings: {
    ...settings,
    passwordPolicy,
    twoFactorAuth,
    licenseEncryption,
    getSecuritySettings,
  }
});

export default api;

